import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { api, ApiError, type CustomLeaderboardRow, type LeaderboardRow } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import PoolNav from '../components/PoolNav.js';
import Avatar from '../components/Avatar.js';
import { formatNumber } from '../lib/intl.js';
import styles from './LeaderboardScreen.module.css';

const RANK_CLASS: Record<number, string | undefined> = { 1: 'rank1', 2: 'rank2', 3: 'rank3' };

function RankingCard({
  row,
  groupId,
  poolId,
}: {
  row: LeaderboardRow;
  groupId: string;
  poolId: string;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [expanded, setExpanded] = useState(false);
  const rankKey = RANK_CLASS[row.rank];
  const rankClassName = rankKey ? styles[rankKey] : '';

  return (
    <div className={styles.cardContainer}>
      {/* The whole card toggles the breakdown — except the avatar, which
          stops the click from reaching this handler and navigates to the
          profile instead. */}
      <div
        className={`${styles.card} ${rankClassName ?? ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={t('leaderboard.toggleBreakdown')}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((e) => !e);
          }
        }}
      >
        <span className={styles.rankPosition}>{row.rank}º</span>
        <Link
          to={`/groups/${groupId}/bolaos/${poolId}/participants/${row.participantId}`}
          className={styles.avatarLink}
          aria-label={row.displayName}
          onClick={(event) => event.stopPropagation()}
        >
          <Avatar name={row.displayName} imageUrl={row.image} size={row.rank <= 3 ? 64 : 44} />
        </Link>
        <div className={styles.info}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{row.displayName}</span>
            {row.isYou ? <span className={styles.youTag}>{t('group.you')}</span> : null}
          </div>
          <div className={styles.stats}>
            <span className={styles.statBadge}>
              {t('leaderboard.exactHits', { count: row.exact })}
            </span>
            <span className={styles.statBadge}>
              {t('leaderboard.outcomeHits', { count: row.outcome })}
            </span>
            <span className={styles.tierBadge}>{t(`leaderboard.tier.${row.tier}`)}</span>
          </div>
        </div>
        <span className={styles.points}>
          {t('leaderboard.points', {
            count: row.total,
            formattedCount: formatNumber(row.total, locale),
          })}
        </span>
      </div>

      <div className={`${styles.details} ${expanded ? styles.detailsExpanded : ''}`}>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsOutcome')}</span>
          <strong>{row.outcome}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsExact')}</span>
          <strong>{row.exact}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsProximity')}</span>
          <strong>{row.proximity}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsPenalty')}</span>
          <strong>{row.penalty}</strong>
        </div>
      </div>
    </div>
  );
}

/**
 * A custom competition's row (PLAN.md Phase 10.4). Its breakdown merges a
 * `football`-kind question's outcome/exact/proximity/penalty components
 * with `row.otherPoints` — everything else (multiple_choice questions) —
 * rather than the real-football card's four-way split alone. No avatar
 * link — there's no per-participant profile screen for a custom competition
 * yet, so linking one would just 404/error.
 */
function CustomRankingCard({ row }: { row: CustomLeaderboardRow }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const [expanded, setExpanded] = useState(false);
  const rankKey = RANK_CLASS[row.rank];
  const rankClassName = rankKey ? styles[rankKey] : '';

  return (
    <div className={styles.cardContainer}>
      <div
        className={`${styles.card} ${rankClassName ?? ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={t('leaderboard.toggleBreakdown')}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setExpanded((e) => !e);
          }
        }}
      >
        <span className={styles.rankPosition}>{row.rank}º</span>
        <Avatar name={row.displayName} imageUrl={row.image} size={row.rank <= 3 ? 64 : 44} />
        <div className={styles.info}>
          <div className={styles.nameRow}>
            <span className={styles.name}>{row.displayName}</span>
            {row.isYou ? <span className={styles.youTag}>{t('group.you')}</span> : null}
          </div>
          <div className={styles.stats}>
            <span className={styles.statBadge}>
              {t('leaderboard.correctCount', { count: row.correctCount })}
            </span>
            <span className={styles.tierBadge}>{t(`leaderboard.tier.${row.tier}`)}</span>
          </div>
        </div>
        <span className={styles.points}>
          {t('leaderboard.points', {
            count: row.total,
            formattedCount: formatNumber(row.total, locale),
          })}
        </span>
      </div>

      <div className={`${styles.details} ${expanded ? styles.detailsExpanded : ''}`}>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsOutcome')}</span>
          <strong>{row.outcome}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsExact')}</span>
          <strong>{row.exact}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsProximity')}</span>
          <strong>{row.proximity}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsPenalty')}</span>
          <strong>{row.penalty}</strong>
        </div>
        <div className={styles.detailsRow}>
          <span>{t('leaderboard.detailsOtherPoints')}</span>
          <strong>{row.otherPoints}</strong>
        </div>
      </div>
    </div>
  );
}

export default function LeaderboardScreen() {
  const { t } = useTranslation();
  const { id = '', poolId = '' } = useParams();

  const { data: pool } = useQuery({
    queryKey: ['pool', id, poolId],
    queryFn: () => api.getPool(id, poolId),
  });
  const isCustom = pool?.sportId === 'custom';

  const football = useQuery({
    queryKey: ['leaderboard', id, poolId],
    queryFn: () => api.getLeaderboard(id, poolId),
    enabled: !!pool && !isCustom,
  });
  const custom = useQuery({
    queryKey: ['custom-leaderboard', id, poolId],
    queryFn: () => api.getCustomLeaderboard(id, poolId),
    enabled: !!pool && isCustom,
  });

  const isPending = !pool || (isCustom ? custom.isPending : football.isPending);
  const isError = isCustom ? custom.isError : football.isError;
  const error = isCustom ? custom.error : football.error;

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="screen">
        <PoolNav groupId={id} poolId={poolId} />
        <p className="error">
          {error instanceof ApiError ? error.message : t('common.somethingWentWrong')}
        </p>
      </main>
    );
  }

  const rows = isCustom ? custom.data?.rows : football.data?.rows;

  return (
    <main className="screen">
      <AppHeader groupId={id} poolId={poolId} />
      <PoolNav groupId={id} poolId={poolId} />

      {/* The points-evolution chart only exists for football's matches. */}
      {isCustom ? null : (
        <Link to={`/groups/${id}/bolaos/${poolId}/chart`} className="button button-secondary">
          {t('chart.title')}
        </Link>
      )}

      {!rows || rows.length === 0 ? (
        <p className="muted">{t('leaderboard.empty')}</p>
      ) : (
        <div className={styles.list}>
          {isCustom
            ? (rows as CustomLeaderboardRow[]).map((row) => (
                <CustomRankingCard key={row.participantId} row={row} />
              ))
            : (rows as LeaderboardRow[]).map((row) => (
                <RankingCard key={row.participantId} row={row} groupId={id} poolId={poolId} />
              ))}
        </div>
      )}
    </main>
  );
}
